import { IContentControlManager } from 'app/js/src/domain/content-control/manager';
import { IContentControlVideoSource } from 'app/js/src/domain/content-control/i-content-control-video-source';
import { IContentControlStudioTimeline } from "../content-control-studio-timeline/i-content-control-studio-timeline";
import { IStudioViewModelContext } from "./i-studio-view-model-context";

export class StudioTimelineZoomManager {
  isFitSegment = false;
  isLiveZoomMode = false;
  availableTimelineZoomSteps = [1, 2, 3, 4, 5, 10, 30, 60, 300, 600, 900, 1800, 3600, 7200];
  timelineZoomSteps: number[] = [];
  selectedTimelineZoomStepIndex = this.availableTimelineZoomSteps.findIndex((x) => x == 600);
  selectedTimelineBlockDuration = 600;

  constructor(
    private readonly _timeline: IContentControlStudioTimeline,
    private readonly _video: IContentControlVideoSource,
    private readonly _manager: IContentControlManager,
    private readonly _context: IStudioViewModelContext,
  ) {}

  timelineZoom(change: number): void {
    if (this.isFitSegment) return;
    this.isLiveZoomMode = false;
    const newIndex = this.selectedTimelineZoomStepIndex + change;
    if (newIndex >= 0 && newIndex <= this.timelineZoomSteps.length - 1) {
      this.selectedTimelineZoomStepIndex = newIndex;
      this.selectedTimelineBlockDuration = this.timelineZoomSteps[this.selectedTimelineZoomStepIndex];
      this._context.markForCheck();
      setTimeout(() => this._timeline.onChangeEffectingTimeline(), 0);
    }
  }

  enterLiveZoomMode(): void {
    this.isFitSegment = false;
    this.isLiveZoomMode = true;
    // 20s/block × 6 blocks = 120s = 2 min visible range
    this.selectedTimelineBlockDuration = 20;
    this.timelineZoomSteps = [...this.availableTimelineZoomSteps.filter((z) => z < 20), 20];
    this.selectedTimelineZoomStepIndex = this.timelineZoomSteps.length - 1;
    this._context.markForCheck();
    setTimeout(() => this._timeline.onChangeEffectingTimeline(), 0);
  }

  exitLiveZoomMode(): void {
    this.isLiveZoomMode = false;
    const timeline = this._timeline;
    this.calculateZoomLevels(timeline.currentVideoTimeSecs(timeline.isFitSegment, timeline.isEditedTimeline));
    this._context.markForCheck();
    setTimeout(() => timeline.onChangeEffectingTimeline(), 0);
  }

  timelineZoomFitVideo(): void {
    this.isFitSegment = false;
    this.isLiveZoomMode = false;
    this.calculateZoomLevels(this._timeline.currentVideoTimeSecs(this._timeline.isFitSegment, this._timeline.isEditedTimeline));
    this._context.markForCheck();
    setTimeout(() => this._timeline.onChangeEffectingTimeline(), 0);
  }

  timelineZoomFitSegment(goTo: "in" | "out" = "in"): void {
    const manager = this._manager;
    if (manager?.selectedSegment) {
      this.isFitSegment = true;
      const start = manager.selectedSegment.startsAtMs / 1000;
      const end = manager.selectedSegment.endsAtMs / 1000;
      let segmentDuration = end - start;
      const video = this._video;
      let segmentMargin = segmentDuration / 6;
      segmentDuration += segmentMargin <= start ? segmentMargin : start;
      segmentDuration += segmentMargin <= video.durationSecs - end ? segmentMargin : video.durationSecs - end;
      this.calculateZoomLevels(segmentDuration);
      this._context.markForCheck();
      setTimeout(() => {
        this._timeline.onChangeEffectingTimeline();
        this._timeline.goToSelectedEventTime(goTo);
      }, 0);
    }
  }

  goToIn(): void {
    this._timeline.goToSelectedEventTime("in");
  }

  goToOut(): void {
    this._timeline.goToSelectedEventTime("out");
  }

  calculateZoomLevels(time: number): void {
    this.selectedTimelineBlockDuration = this.roundDown(time / 6);
    if (this.isFitSegment) return;
    this.timelineZoomSteps = this.availableTimelineZoomSteps.filter(
      (z) => z < this.selectedTimelineBlockDuration,
    );
    this.timelineZoomSteps.push(this.selectedTimelineBlockDuration);
    this.selectedTimelineZoomStepIndex = this.timelineZoomSteps.length - 1;
  }

  private roundDown(num: number): number {
    if (num < 10) return num;
    else if (num < 100) return Math.floor(num / 10) * 10;
    else if (num < 1000) return Math.floor(num / 100) * 100;
    else if (num < 10000) return Math.floor(num / 1000) * 1000;
    else if (num < 100000) return Math.floor(num / 10000) * 10000;
    else return num;
  }
}
